// src/pages/admin/AdminCachePage.tsx
// 書影（/ogp）キャッシュの状態確認・再取得画面。
//
// 背景: /ogp のCloudFrontキャッシュはクエリ文字列込みでキーになっているため、
// 同じ本でもURLの組み立て方が違うと別々のキャッシュエントリになる
// （[[project_cover_url_ogp_cache_consistency]] 参照）。現行のWeb/モバイルは
// rl_book_id があれば `?rl_book_id=X` に統一済みだが、アップデート前の
// モバイルアプリは `?rl_book_id=X&title=Y` や `?isbn=X&title=Y` のような
// 旧パターンで叩いている可能性が残る。この画面では本のrl_book_id/ISBN/タイトルから
// 想定されるURLパターンを機械的に列挙し、それぞれ個別に状態確認・再取得できるようにする。

import React, { useState } from 'react';
import axios from 'axios';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import { useAuth } from '../../context/AuthContext';
import { useAppTokens } from '../../context/ThemeModeContext';
import { NEW_API, buildOgpUrlByRlBookId, buildOgpUrlByTitle } from '../../config/api';
import AdminNav from './AdminNav';

interface CacheCheckResult {
  url: string;
  status_code: number;
  x_cache: string;
  age_seconds: string | null;
  cache_control: string | null;
  content_type: string;
  content_length: string | null;
  source: string;
  checked_at: number;
}

interface HistoryEntry {
  // このチェックが何のタイミングでの確認かを示すラベル（UI表示用）
  eventLabel: string;
  result: CacheCheckResult;
}

interface UrlRow {
  id: string;
  label: string;
  url: string;
  history: HistoryEntry[];
  loading: boolean;
  loadingStep: string | null;
  error: string | null;
}

const buildCandidateRows = (rlBookId: string, isbn: string, title: string): UrlRow[] => {
  const rows: { label: string; url: string }[] = [];
  const rlBookIdNum = rlBookId.trim() ? Number(rlBookId.trim()) : null;
  const isbnTrim = isbn.trim();
  const titleTrim = title.trim();

  if (rlBookIdNum != null && !Number.isNaN(rlBookIdNum)) {
    rows.push({ label: '現行（Web/モバイル共通・rl_book_idのみ）', url: buildOgpUrlByRlBookId(rlBookIdNum) });
    if (titleTrim) {
      rows.push({
        label: '旧モバイル版（rl_book_id + title）',
        url: `${NEW_API.OGP}?rl_book_id=${rlBookIdNum}&title=${encodeURIComponent(titleTrim)}`,
      });
    }
  }

  if (titleTrim) {
    rows.push({ label: '現行（title + isbn フォールバック）', url: buildOgpUrlByTitle(titleTrim, isbnTrim || undefined) });
    if (isbnTrim) {
      rows.push({
        label: '旧モバイル版（isbn + title 順）',
        url: `${NEW_API.OGP}?isbn=${encodeURIComponent(isbnTrim)}&title=${encodeURIComponent(titleTrim)}`,
      });
    }
    if (!isbnTrim) {
      rows.push({ label: 'title単独', url: `${NEW_API.OGP}?title=${encodeURIComponent(titleTrim)}` });
    }
  }

  return rows.map((r, i) => ({
    id: `${Date.now()}-${i}`,
    label: r.label,
    url: r.url,
    history: [],
    loading: false,
    loadingStep: null,
    error: null,
  }));
};

const AdminCachePage: React.FC = () => {
  const TOKENS = useAppTokens();
  const { idToken } = useAuth();

  const [rlBookId, setRlBookId] = useState('');
  const [isbn, setIsbn] = useState('');
  const [title, setTitle] = useState('');
  const [manualUrl, setManualUrl] = useState('');
  const [rows, setRows] = useState<UrlRow[]>([]);

  const cardSx = {
    border: `1px solid ${TOKENS.border}`,
    background: TOKENS.glass,
    borderRadius: '18px',
    p: 2.5,
  } as const;

  const inputSx = {
    '& .MuiOutlinedInput-root': { color: TOKENS.text, background: TOKENS.buttonBg, borderRadius: '12px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: TOKENS.borderSoft },
    '& .MuiInputLabel-root': { color: TOKENS.textSub },
  } as const;

  const handleGenerateCandidates = () => {
    const candidates = buildCandidateRows(rlBookId, isbn, title);
    if (candidates.length === 0) return;
    setRows((prev) => [...prev, ...candidates]);
  };

  const handleAddManualUrl = () => {
    const url = manualUrl.trim();
    if (!url) return;
    setRows((prev) => [
      ...prev,
      { id: `${Date.now()}-manual`, label: '手動追加', url, history: [], loading: false, loadingStep: null, error: null },
    ]);
    setManualUrl('');
  };

  const handleRemoveRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const checkOnce = async (url: string): Promise<CacheCheckResult> => {
    const res = await axios.post(
      NEW_API.ADMIN,
      { operation_type: 'check_ogp_cache', url },
      { headers: { Authorization: `Bearer ${idToken}` } },
    );
    return res.data?.data;
  };

  const appendHistory = (rowId: string, entry: HistoryEntry) => {
    setRows((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, history: [...r.history, entry] } : r)),
    );
  };

  const setLoadingStep = (rowId: string, step: string | null) => {
    setRows((prev) => prev.map((r) => (r.id === rowId ? { ...r, loading: step != null, loadingStep: step } : r)));
  };

  // 「状態を確認」: パージは行わず、今この瞬間のキャッシュ状態を1回だけ見る
  const runCheck = async (rowId: string) => {
    if (!idToken) return;
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;
    setLoadingStep(rowId, '確認中…');
    try {
      const result = await checkOnce(row.url);
      appendHistory(rowId, { eventLabel: '確認', result });
    } catch (e: any) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? { ...r, error: e?.response?.data?.error?.message || '確認に失敗しました' }
            : r,
        ),
      );
    } finally {
      setLoadingStep(rowId, null);
    }
  };

  // 「再取得する」: ①パージ直前の状態→②パージ実行→③パージ直後の状態（必ずMissになるのが
  // 正常）→④もう一度アクセスして再キャッシュされたことを確認（Hitに戻れば成功）、という
  // 4ステップを自動で行い、それぞれの結果を履歴に残す。パージ直後にMissが出るのは
  // 「パージが効いた証拠」であって異常ではないため、その旨がラベルで分かるようにする
  const runPurgeThenCheck = async (rowId: string) => {
    if (!idToken) return;
    const row = rows.find((r) => r.id === rowId);
    if (!row) return;

    try {
      setLoadingStep(rowId, 'パージ前の状態を確認中…');
      const before = await checkOnce(row.url);
      appendHistory(rowId, { eventLabel: 'パージ前', result: before });

      setLoadingStep(rowId, 'CloudFrontキャッシュをパージ中…');
      await axios.post(
        NEW_API.ADMIN,
        { operation_type: 'purge_ogp_cache', url: row.url },
        { headers: { Authorization: `Bearer ${idToken}` } },
      );
      // invalidationの反映を少し待つ
      await new Promise((resolve) => setTimeout(resolve, 3000));

      setLoadingStep(rowId, 'パージ直後の状態を確認中…（Missになれば正常）');
      const afterPurge = await checkOnce(row.url);
      appendHistory(rowId, { eventLabel: 'パージ直後（Missが正常）', result: afterPurge });

      // afterPurgeのリクエスト自体がオリジンへの新規アクセスとして扱われ、次のリクエスト
      // からはそれがキャッシュされてHitになるはず。少し待ってもう一度確認して見せる
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setLoadingStep(rowId, '再キャッシュされたか確認中…');
      const afterRecache = await checkOnce(row.url);
      appendHistory(rowId, { eventLabel: '再アクセス後（Hitに戻れば完了）', result: afterRecache });
    } catch (e: any) {
      setRows((prev) =>
        prev.map((r) =>
          r.id === rowId
            ? { ...r, error: e?.response?.data?.error?.message || '再取得に失敗しました' }
            : r,
        ),
      );
    } finally {
      setLoadingStep(rowId, null);
    }
  };

  return (
    <Box sx={{ minHeight: '100dvh', background: TOKENS.bg, color: TOKENS.text }}>
      <AdminNav />
      <Box sx={{ maxWidth: 960, margin: 'auto', px: 2, py: 4 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 0.5 }}>書影キャッシュの再取得</Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, mb: 3 }}>
          読み込みに失敗している本の rl_book_id / ISBN / タイトルを入力すると、Web・モバイルが
          使う可能性のあるURLパターンを列挙します。URLごとに現在のキャッシュ状態を確認したり、
          再取得（CloudFrontキャッシュのパージ）したりできます。
        </Typography>

        <Box sx={{ ...cardSx, mb: 3 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.5 }}>候補URLを生成</Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1.5 }}>
            <TextField
              size="small"
              label="rl_book_id（任意）"
              value={rlBookId}
              onChange={(e) => setRlBookId(e.target.value)}
              sx={{ ...inputSx, width: 180 }}
            />
            <TextField
              size="small"
              label="ISBN13（任意）"
              value={isbn}
              onChange={(e) => setIsbn(e.target.value)}
              sx={{ ...inputSx, width: 200 }}
            />
            <TextField
              size="small"
              label="タイトル（任意）"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              sx={{ ...inputSx, flexGrow: 1, minWidth: 220 }}
            />
          </Box>
          <Button
            onClick={handleGenerateCandidates}
            sx={{
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 700,
              px: 2.5,
              color: TOKENS.bg,
              background: TOKENS.text,
              '&:hover': { background: TOKENS.textSub },
            }}
          >
            候補URLを追加
          </Button>

          <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${TOKENS.border}` }}>
            <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mb: 1 }}>
              または /ogp のURLを直接指定
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="https://api.readlinker.com/ogp?..."
                value={manualUrl}
                onChange={(e) => setManualUrl(e.target.value)}
                sx={{ ...inputSx, flexGrow: 1 }}
              />
              <Button
                onClick={handleAddManualUrl}
                sx={{ textTransform: 'none', color: TOKENS.text, border: `1px solid ${TOKENS.borderSoft}`, borderRadius: '10px' }}
              >
                追加
              </Button>
            </Box>
          </Box>
        </Box>

        {rows.length === 0 ? (
          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub, textAlign: 'center', mt: 4 }}>
            まだURLがありません。上のフォームから候補URLを追加してください。
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {rows.map((row) => (
              <Box key={row.id} sx={cardSx}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 12, color: TOKENS.textSub }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: 12, wordBreak: 'break-all', fontFamily: 'monospace' }}>
                      {row.url}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    onClick={() => handleRemoveRow(row.id)}
                    sx={{ textTransform: 'none', color: TOKENS.textSub, flexShrink: 0, minWidth: 0 }}
                  >
                    削除
                  </Button>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    onClick={() => runCheck(row.id)}
                    disabled={row.loading}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '999px',
                      color: TOKENS.text,
                      background: TOKENS.buttonBg,
                      border: `1px solid ${TOKENS.borderSoft}`,
                      '&:hover': { borderColor: TOKENS.accent },
                    }}
                  >
                    状態を確認
                  </Button>
                  <Button
                    size="small"
                    onClick={() => runPurgeThenCheck(row.id)}
                    disabled={row.loading}
                    sx={{
                      textTransform: 'none',
                      borderRadius: '999px',
                      color: TOKENS.bg,
                      background: TOKENS.text,
                      '&:hover': { background: TOKENS.textSub },
                    }}
                  >
                    再取得する（パージ→確認）
                  </Button>
                  {row.loading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CircularProgress size={13} sx={{ color: TOKENS.textSub }} />
                      <Typography sx={{ fontSize: 11.5, color: TOKENS.textSub }}>{row.loadingStep}</Typography>
                    </Box>
                  )}
                </Box>

                {row.error && (
                  <Typography sx={{ fontSize: 12, color: '#e57373', mb: 1 }}>{row.error}</Typography>
                )}

                {row.history.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {row.history.map((h, i) => (
                      <Box
                        key={i}
                        sx={{
                          border: `1px solid ${TOKENS.borderSoft}`,
                          borderRadius: '12px',
                          p: 1.25,
                          width: 170,
                          flexShrink: 0,
                        }}
                      >
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: TOKENS.text, mb: 0.25 }}>
                          {h.eventLabel}
                        </Typography>
                        <Typography sx={{ fontSize: 9.5, color: TOKENS.textSub, mb: 0.5 }}>
                          {new Date(h.result.checked_at * 1000).toLocaleTimeString('ja-JP')}
                        </Typography>
                        <Box
                          component="img"
                          src={`${row.url}${row.url.includes('?') ? '&' : '?'}_preview=${h.result.checked_at}`}
                          alt=""
                          sx={{ width: '100%', height: 80, objectFit: 'contain', background: TOKENS.cardBg, borderRadius: '6px', mb: 0.75 }}
                        />
                        <Chip
                          label={h.result.x_cache}
                          size="small"
                          sx={{
                            fontSize: 10,
                            height: 18,
                            mb: 0.5,
                            background: h.result.x_cache.startsWith('Hit') ? '#1b5e20' : h.result.x_cache.startsWith('Miss') ? '#5c3a00' : TOKENS.buttonBg,
                            color: h.result.x_cache.startsWith('Hit') || h.result.x_cache.startsWith('Miss') ? '#fff' : TOKENS.text,
                          }}
                        />
                        <Typography sx={{ fontSize: 10, color: TOKENS.textSub }}>{h.result.source}</Typography>
                        {h.result.age_seconds != null && (
                          <Typography sx={{ fontSize: 10, color: TOKENS.textSub }}>Age: {h.result.age_seconds}s</Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AdminCachePage;
